/**
 * Region selection model.
 *
 * selection = {
 *   states: { [state.id]: stateObj },   states that have at least one selected city
 *   cities: { [city.id]:  cityObj  },   selected cities
 *   whole:  { [state.id]: stateObj },   states selected as a whole (no individual cities)
 * }
 * City objects carry `state_id` (the numeric id used by the geo endpoints); state objects carry both
 * `id` (database id, used for references) and `state_id`.
 *
 * Saved shape (RegionSettings), confirmed by the backend developer:
 *  - `cities` = every individually selected city.
 *  - `states` = every state that has at least one selected city.
 *  - `wholeStates` = every state selected in full (optional, defaults to []). A state is never in both lists:
 *    the backend rejects that with a 400.
 * Records saved BEFORE `wholeStates` existed marked an entire state as a state with no city of its own;
 * regionFromSaved still reads that form so those records keep working.
 */
export const emptySelection = () => ({ states: {}, cities: {}, whole: {} });

const cloneSel = (s) => ({ states: { ...s.states }, cities: { ...s.cities }, whole: { ...(s.whole || {}) } });

export const citiesOfState = (selection, state) =>
  Object.values(selection.cities).filter((c) => String(c.state_id) === String(state.state_id));

/** 'whole' | 'partial' | 'none' */
export const stateStatus = (selection, state) => {
  if (selection.whole && selection.whole[state.id]) return 'whole';
  return citiesOfState(selection, state).length ? 'partial' : 'none';
};

/** Drop a state entry when none of its cities remain selected. */
const prune = (sel, state) => {
  if (!citiesOfState(sel, state).length) delete sel.states[state.id];
  return sel;
};

/** Toggling a city of a whole-state selection is ignored: the whole state already includes it. */
export function toggleCity(selection, state, city) {
  if (selection.whole && selection.whole[state.id]) return selection;
  const next = cloneSel(selection);
  if (next.cities[city.id]) delete next.cities[city.id];
  else { next.cities[city.id] = city; next.states[state.id] = state; }
  return prune(next, state);
}

export function selectAllInState(selection, state, cities) {
  const next = cloneSel(selection);
  delete next.whole[state.id];
  cities.forEach((c) => { next.cities[c.id] = c; });
  if (cities.length) next.states[state.id] = state;
  return next;
}

/** Select (or unselect) a state as a whole. Selecting it replaces any individual cities of that state. */
export function setWholeState(selection, state, on) {
  const next = cloneSel(selection);
  if (on) {
    citiesOfState(selection, state).forEach((c) => { delete next.cities[c.id]; });
    delete next.states[state.id];
    next.whole[state.id] = state;
  } else {
    delete next.whole[state.id];
  }
  return next;
}

export function clearState(selection, state) {
  const next = cloneSel(selection);
  citiesOfState(selection, state).forEach((c) => { delete next.cities[c.id]; });
  delete next.states[state.id];
  delete next.whole[state.id];
  return next;
}

export const selectedCount = (selection) => Object.keys(selection.cities).length;
export const wholeCount = (selection) => Object.keys(selection.whole || {}).length;
export const stateCount = (selection) => Object.keys(selection.states).length + wholeCount(selection);
export const isEmptySelection = (selection) => selectedCount(selection) === 0 && wholeCount(selection) === 0;

/** Human summary, e.g. "3 cities in 1 state · 1 entire state". */
export function describeSelection(selection) {
  const cities = selectedCount(selection);
  const partial = Object.keys(selection.states).length;
  const whole = wholeCount(selection);
  if (!cities && !whole) return 'Nationwide';
  const parts = [];
  if (cities) parts.push(`${cities} ${cities === 1 ? 'city' : 'cities'} in ${partial} ${partial === 1 ? 'state' : 'states'}`);
  if (whole) parts.push(`${whole} entire ${whole === 1 ? 'state' : 'states'}`);
  return parts.join(' · ');
}

/**
 * Region body: `{ cities: [id], states: [id], wholeStates: [id] }` using the geo records' `id`
 * (not the numeric state_id). No `id` of the region itself: a repeat POST updates the region in place.
 */
export const regionToPayload = (selection) => ({
  states: Object.values(selection.states).map((s) => s.id),
  cities: Object.values(selection.cities).map((c) => c.id),
  wholeStates: Object.values(selection.whole || {}).map((s) => s.id),
});

/** Saved RegionSettings (with resolved references) -> selection. Unresolvable entries are skipped. */
export function regionFromSaved(saved) {
  const sel = emptySelection();
  const cities = ((saved && saved.cities) || []).filter((c) => c && c.id);
  cities.forEach((c) => { sel.cities[c.id] = c; });
  ((saved && saved.wholeStates) || []).forEach((s) => {
    if (s && s.id) sel.whole[s.id] = s;
  });
  ((saved && saved.states) || []).forEach((s) => {
    if (!s || !s.id || sel.whole[s.id]) return;
    const hasCity = cities.some((c) => String(c.state_id) === String(s.state_id));
    if (hasCity) sel.states[s.id] = s;
    else sel.whole[s.id] = s; // legacy form: a saved state with no cities of its own was an entire-state selection
  });
  return sel;
}

const sameKeys = (a, b) => {
  const ka = Object.keys(a || {});
  const kb = Object.keys(b || {});
  return ka.length === kb.length && ka.every((k) => Object.prototype.hasOwnProperty.call(b || {}, k));
};

/** True when two selections choose exactly the same cities, partial states and whole states. */
export const sameSelection = (a, b) => sameKeys(a.cities, b.cities) && sameKeys(a.states, b.states) && sameKeys(a.whole, b.whole);
